/**
 * ExtendStorageModal - Modal para verificar email y extender almacenamiento de clips
 * 
 * Flujo Magic Link:
 * - Usuario ingresa email
 * - Recibe link por correo
 * - Al hacer clic, email se verifica automáticamente
 * - Clips pasan de 24 horas a 3 días
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Clock, Loader2, CheckCircle2, X, 
  Shield, Sparkles, Send, ExternalLink 
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import api from '../services/api';
import { toast } from 'sonner';

export default function ExtendStorageModal({ 
  isOpen, 
  onClose,
  onVerified 
}) {
  const [step, setStep] = useState('intro'); // intro, email, sent
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // Handle send magic link
  const handleSendMagicLink = async () => {
    if (!email.trim() || !email.includes('@')) {
      toast.error('Ingresa un email válido');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/email/send-magic-link', { email: email.trim().toLowerCase() });
      setStep('sent');
      toast.success('Link enviado a tu correo');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || 'Error al enviar');
      } else {
        toast.error('Error al enviar el link');
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend magic link
  const handleResend = async () => {
    setLoading(true);
    try {
      await api.post('/auth/email/send-magic-link', { email: email.trim().toLowerCase() });
      toast.success('Link reenviado');
    } catch (err) {
      toast.error('Error al reenviar');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div 
          initial={{ scale: 0.95, opacity: 0, y: 20 }} 
          animate={{ scale: 1, opacity: 1, y: 0 }} 
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="w-full max-w-md rounded-2xl bg-[#0D121D] border border-white/10 overflow-hidden shadow-2xl"
          data-testid="extend-storage-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#131B2A] to-[#0D121D] p-6 text-white border-b border-white/10">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#00F0FF]" />
                  <Sparkles className="h-5 w-5 text-[#BBFF00]" />
                </div>
                <h2 className="text-2xl font-display font-bold mt-2 text-slate-50">
                  {step === 'sent' ? 'Revisa tu correo' : 'Extiende tu almacenamiento'}
                </h2>
                <p className="text-slate-400 mt-1">
                  {step === 'sent' 
                    ? 'Te enviamos un link de verificación' 
                    : 'De 24 horas a 3 días gratis'}
                </p>
              </div>
              <button 
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white"
                data-testid="extend-storage-close-icon-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 bg-[#0D121D]">
            {/* Step: Intro */}
            {step === 'intro' && (
              <div className="space-y-4" data-testid="extend-intro-step">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#131B2A] rounded-xl p-4 text-center border border-white/10">
                    <Clock className="h-8 w-8 mx-auto text-slate-500" />
                    <p className="text-2xl font-bold mt-2 text-slate-300">24h</p>
                    <p className="text-xs text-slate-500">Ahora</p>
                  </div>
                  <div className="bg-[#BBFF00]/10 rounded-xl p-4 text-center border-2 border-[#BBFF00]">
                    <Sparkles className="h-8 w-8 mx-auto text-[#BBFF00]" />
                    <p className="text-2xl font-bold mt-2 text-[#BBFF00]">3 días</p>
                    <p className="text-xs text-[#BBFF00]">Con email</p>
                  </div>
                </div>

                <div className="bg-[#00F0FF]/10 rounded-xl p-4 border border-[#00F0FF]/30">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-100 text-sm">
                        ¿Por qué verificar tu email?
                      </p>
                      <ul className="text-sm text-slate-400 mt-2 space-y-1">
                        <li>• Tus clips duran <strong className="text-[#BBFF00]">3 días</strong> en vez de 24 horas</li>
                        <li>• Puedes descargar y compartir clips</li>
                        <li>• Recuperar contraseña si la olvidas</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => setStep('email')}
                  className="w-full h-12 bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold shadow-[0_0_20px_rgba(187,255,0,0.3)] transition-all"
                  data-testid="start-email-verify-button"
                >
                  <Mail className="h-5 w-5 mr-2" />
                  Verificar mi email
                </Button>

                <button 
                  onClick={onClose}
                  className="w-full text-sm text-slate-500 hover:text-slate-300 py-2 transition-colors"
                  data-testid="extend-storage-not-now-button"
                >
                  Ahora no
                </button>
              </div>
            )}

            {/* Step: Email Input */}
            {step === 'email' && (
              <div className="space-y-4" data-testid="email-input-step">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Tu email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="h-12 text-lg bg-[#131B2A] border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#BBFF00] focus:ring-[#BBFF00]/20"
                    data-testid="email-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleSendMagicLink()}
                  />
                  <p className="text-xs text-slate-500">
                    Te enviaremos un link para verificar tu correo
                  </p>
                </div>

                <Button 
                  onClick={handleSendMagicLink}
                  disabled={loading}
                  className="w-full h-12 bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold shadow-[0_0_20px_rgba(187,255,0,0.3)] transition-all"
                  data-testid="send-magic-link-button"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Enviar link
                    </>
                  )}
                </Button>

                <button 
                  onClick={() => setStep('intro')}
                  className="w-full text-sm text-slate-500 hover:text-slate-300 py-2 transition-colors"
                  data-testid="extend-storage-back-button"
                >
                  Volver
                </button>
              </div>
            )}

            {/* Step: Link Sent */}
            {step === 'sent' && (
              <div className="space-y-5" data-testid="magic-link-sent-step">
                <div className="text-center">
                  <div className="bg-[#BBFF00]/20 rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-4">
                    <Mail className="h-10 w-10 text-[#BBFF00]" />
                  </div>
                  <p className="text-slate-400 text-sm">
                    Enviamos un link de verificación a
                  </p>
                  <p className="font-semibold text-lg text-slate-100">{email}</p>
                </div>

                <div className="bg-[#00F0FF]/10 rounded-xl p-4 border border-[#00F0FF]/30">
                  <div className="flex items-start gap-3">
                    <ExternalLink className="h-5 w-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-300">
                        <strong>Haz clic en el link</strong> del correo para verificar automáticamente.
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        El link expira en 24 horas
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
                  <p className="text-sm text-amber-200">
                    <strong>¿No lo ves?</strong> Revisa tu carpeta de spam o promociones
                  </p>
                </div>

                <Button 
                  onClick={onClose}
                  className="w-full h-12 bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold shadow-[0_0_20px_rgba(187,255,0,0.3)] transition-all"
                  data-testid="close-modal-button"
                >
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  Entendido
                </Button>

                <button 
                  onClick={handleResend}
                  disabled={loading}
                  className="w-full text-sm text-[#BBFF00] hover:text-[#A2DC00] disabled:text-slate-600 py-2 transition-colors"
                  data-testid="resend-magic-link-button"
                >
                  {loading ? 'Enviando...' : 'Reenviar link'}
                </button>

                <button 
                  onClick={() => {
                    setStep('email');
                    setEmail('');
                  }}
                  className="w-full text-sm text-slate-500 hover:text-slate-300 py-1 transition-colors"
                  data-testid="use-another-email-button"
                >
                  Usar otro email
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
